import { Visibility, WarningAmber } from "@mui/icons-material";
import { Box, Chip, IconButton, TableCell, TableRow } from "@mui/material";
import React from "react";
import { t } from "ttag";
import Tooltip from "../../components/Tooltip/LightTooltip";
import AuditTableShell from "./AuditTableShell";
import useStyles from "./EdgeAudits.style";
import { getDeviceTelemetryRecoveryAudits } from "./edgeAuditsApi";
import {
  formatDurationMs,
  formatTs,
  ITelemetryRecoveryAudit,
  outcomeLabel,
  skipReasonLabel,
  statusChip,
  yesNo
} from "./edgeAuditsUtils";
import { usePagedAudits } from "./usePagedAudits";

const COLUMN_COUNT = 12;

interface ITelemetryRecoveryTableProps {
  deviceId: string;
  onOpenAudit: (audit: ITelemetryRecoveryAudit) => void;
  refreshKey?: number;
}

/** CM-731: one row per telemetry recovery run, newest first. */
const TelemetryRecoveryTable = ({ deviceId, onOpenAudit, refreshKey = 0 }: ITelemetryRecoveryTableProps) => {
  const classes = useStyles();
  const paged = usePagedAudits(getDeviceTelemetryRecoveryAudits, deviceId, refreshKey);

  const renderStatus = (audit: ITelemetryRecoveryAudit) => {
    const chip = statusChip(audit);
    return <Chip label={chip.label} size="small" color={chip.tone} variant="outlined" />;
  };

  const renderGaps = (audit: ITelemetryRecoveryAudit) => (
    <Box display="flex" alignItems="center" gap={0.5}>
      <span>{audit.totals?.gaps ?? 0}</span>
      {audit.gapsTruncated && (
        <Tooltip title={t`Only a capped subset of the gap rows was stored; totals reflect all of them.`} placement="top">
          <WarningAmber className={classes.warnIcon} />
        </Tooltip>
      )}
    </Box>
  );

  const header = (
    <TableRow>
      <TableCell className={classes.headCell}>{t`STARTED AT`}</TableCell>
      <TableCell className={classes.headCell}>{t`FINISHED AT`}</TableCell>
      <TableCell className={classes.headCell}>{t`DURATION`}</TableCell>
      <TableCell className={classes.headCell}>{t`OUTCOME`}</TableCell>
      <TableCell className={classes.headCell}>{t`SKIP REASON`}</TableCell>
      <TableCell className={classes.headCell}>{t`STATS CHECKED`}</TableCell>
      <TableCell className={classes.headCell}>{t`GAPS`}</TableCell>
      <TableCell className={classes.headCell}>{t`STATUS`}</TableCell>
      <TableCell className={classes.headCell}>{t`SD CARD`}</TableCell>
      <TableCell className={classes.headCell}>{t`CACHING`}</TableCell>
      <TableCell className={classes.headCell}>{t`DM INSTANCE`}</TableCell>
      <TableCell align="center" className={classes.headCell}>{t`VIEW`}</TableCell>
    </TableRow>
  );

  return (
    <AuditTableShell
      header={header}
      rowCount={paged.rows.length}
      colSpan={COLUMN_COUNT}
      emptyText={t`No telemetry recovery audits for this device yet.`}
      loading={paged.loading}
      totalCount={paged.totalCount}
      page={paged.page}
      pageSize={paged.pageSize}
      onPageChange={paged.handlePageChange}
      onPageSizeChange={paged.handlePageSizeChange}
    >
      {paged.rows.map((audit: ITelemetryRecoveryAudit) => (
        <TableRow hover key={audit.id} className={classes.row}>
          <TableCell className={classes.cell}>{formatTs(audit.startedTs)}</TableCell>
          <TableCell className={classes.cell}>{formatTs(audit.finishedTs)}</TableCell>
          <TableCell className={classes.cell}>{formatDurationMs(audit.durationMs)}</TableCell>
          <TableCell className={classes.cell}>{outcomeLabel(audit)}</TableCell>
          <TableCell className={classes.cell}>
            {audit.outcome === "skipped" ? skipReasonLabel(audit.skipReason) : "-"}
          </TableCell>
          <TableCell className={classes.cell}>{audit.statsChecked?.length ? audit.statsChecked.join(", ") : "-"}</TableCell>
          <TableCell className={classes.numCell}>{renderGaps(audit)}</TableCell>
          <TableCell className={classes.cell}>{renderStatus(audit)}</TableCell>
          <TableCell className={classes.cell}>{yesNo(audit.sdCardConnected)}</TableCell>
          <TableCell className={classes.cell}>{yesNo(audit.isCachingEnabled)}</TableCell>
          <TableCell className={classes.cell}>{audit.dmid || "-"}</TableCell>
          <TableCell align="center" className={classes.cell}>
            <Tooltip title={t`View the gap tables for this run`} placement="top">
              <IconButton size="small" onClick={() => onOpenAudit(audit)}>
                <Visibility className={classes.viewIcon} />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      ))}
    </AuditTableShell>
  );
};

export default TelemetryRecoveryTable;
